/**
 * Settings → System → Ask Pegasus (ASK-REVIEW-01 + ASK-KNOW-01, 2026-09-23).
 *
 * Seth: "Where are the ask pegasus questions saved - this is a good idea.
 * What if there is something we want to have added to the ask pegasus
 * knowledge base or update or revise or change?"
 *
 * One page: what Ask Pegasus reads, every question people asked (from
 * pegasus_assistant_log, written by the pegasus-assistant function), and
 * the fix for a wrong or missing answer — "Teach it" writes a Library note
 * that Ask Pegasus reads first. Revising knowledge = edit the note or upload
 * a new version of the document in Team → Library.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUpRight, Check, ChevronDown, ChevronRight, StickyNote, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { supabase } from "@/lib/supabase";
import { InfoTip } from "@/components/InfoTip";
import { NoteEditorModal, NOTES_FOLDER } from "@/features/team/LibraryNote";
import { useLibraryDocs } from "@/features/team/team";

interface LogRow {
  id: number;
  at: string;
  user_email: string | null;
  path: string | null;
  question: string | null;
  answer: string | null;
  tools: { tool: string; args?: Record<string, unknown> }[] | null;
  ms: number | null;
  ok: boolean | null;
  verdict: "right" | "wrong" | null;
  verdict_by: string | null;
  handled_at: string | null;
  handled_by: string | null;
  note_id: string | null;
}

const TOOL_LABEL: Record<string, string> = {
  search_customers: "Customers", customer_snapshot: "Customers", board_day: "Board", jobs_open: "Jobs",
  sub_invoices: "Sub Invoices", agreements_expiring: "Agreements", library_docs: "Library", tech_stats: "Tech Stats",
  purchase_orders: "Purchase Orders", vendors: "Vendors", team_directory: "Team Roster", service_fees: "Service Fees",
  pricebook: "Pricebook", inspection_info: "Inspection Phones", office_notes: "Notes",
};

/** "It couldn't find it" answers deserve a look even when nobody flagged them. */
const COULDNT = /\b(couldn'?t|could not|can'?t find|cannot find|isn'?t (in|on)|not (in|on) (the|our|any)|don'?t see|don'?t have|no (record|results?|matches?|information)|outside (of )?(what|the records?|anything) i (can )?see|ran out of lookup|hit a snag)\b/i;

const when = (ts: string) =>
  new Date(ts).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });

/** Answers carry [label](path) links and **bold** — show them as plain words here. */
const plain = (s: string | null) => (s ?? "").replace(/\[([^\]]+)\]\([^)]*\)/g, "$1").replace(/\*\*(.+?)\*\*/g, "$1");

function useAssistantLog() {
  return useQuery({
    queryKey: ["assistant_log"],
    staleTime: 30 * 1000,
    queryFn: async (): Promise<LogRow[]> => {
      const { data, error } = await supabase.from("pegasus_assistant_log")
        .select("id, at, user_email, path, question, answer, tools, ms, ok, verdict, verdict_by, handled_at, handled_by, note_id")
        .order("at", { ascending: false }).limit(200);
      if (error) throw error;
      return (data ?? []) as LogRow[];
    },
  });
}

function useLibraryStats() {
  return useQuery({
    queryKey: ["library_stats"],
    staleTime: 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.from("pegasus_library_doc")
        .select("kind, storage_path, external_url, content_extracted_at, filename").eq("active", true);
      const rows = (data ?? []) as { kind: string | null; storage_path: string | null; external_url: string | null; content_extracted_at: string | null; filename: string }[];
      const files = rows.filter((r) => r.kind !== "note");
      return {
        files: files.length,
        readable: files.filter((r) => r.content_extracted_at).length,
        notes: rows.filter((r) => r.kind === "note").length,
        driveOnly: files.filter((r) => !r.storage_path && r.external_url).map((r) => r.filename).sort(),
      };
    },
  });
}

export function AskPegasusAdmin() {
  const qc = useQueryClient();
  const log = useAssistantLog();
  const stats = useLibraryStats().data;
  const libDocs = useLibraryDocs().data;
  const folders = useMemo(() => [...new Set([NOTES_FOLDER, ...(libDocs ?? []).map((d) => d.folder)])].sort((a, b) => a.localeCompare(b)), [libDocs]);
  const [filter, setFilter] = useState<"look" | "all">("look");
  const [open, setOpen] = useState<number | null>(null);
  const [teach, setTeach] = useState<LogRow | null>(null);
  const [newNote, setNewNote] = useState(false);
  const [showDrive, setShowDrive] = useState(false);

  const rows = log.data ?? [];
  const needsLook = (r: LogRow) => !r.handled_at && r.verdict !== "right" && (r.verdict === "wrong" || COULDNT.test(r.answer ?? "") || r.ok === false);
  const lookCount = rows.filter(needsLook).length;
  const shown = useMemo(() => (filter === "look" ? rows.filter(needsLook) : rows), [rows, filter]); // eslint-disable-line react-hooks/exhaustive-deps

  const refresh = () => qc.invalidateQueries({ queryKey: ["assistant_log"] });
  const verdict = useMutation({
    mutationFn: async (v: { id: number; verdict: "right" | "wrong" | null }) => {
      const { error } = await supabase.rpc("assistant_log_feedback", { p_id: v.id, p_verdict: v.verdict });
      if (error) throw error;
    },
    onSuccess: refresh,
  });
  const handle = useMutation({
    mutationFn: async (v: { id: number; noteId?: string | null }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? null;
      const { error } = await supabase.from("pegasus_assistant_log")
        .update({ handled_at: new Date().toISOString(), handled_by: who, ...(v.noteId ? { note_id: v.noteId } : {}) })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: refresh,
  });

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-hairline bg-white p-5 shadow-e1">
        <h3 className="flex items-center gap-2 text-[13px] font-bold text-slate-800">
          What It Reads
          <InfoTip label="How to change what it knows">
            Write or edit a note for quick answers, or upload a new version of a document with the same name and it replaces
            the old one. Fix a record where it lives and Ask Pegasus sees the change right away.
          </InfoTip>
        </h3>
        <div className="mt-2 space-y-2 text-[13px] leading-relaxed text-slate-600">
          <p>
            <b>The Team Library</b>
            {stats && <> — {stats.files} documents ({stats.readable} it can read inside) and {stats.notes} note{stats.notes === 1 ? "" : "s"}</>}.
          </p>
          <p className="flex items-center gap-1.5">
            <b>The records</b>
            <InfoTip label="Which records it reads">
              Customers and their visits, the board, open install jobs, agreements, purchase orders, vendors, the Team Roster,
              Service Fees, the Pricebook, city inspection phones and tech productivity.
            </InfoTip>
          </p>
          {stats && stats.driveOnly.length > 0 && (
            <div className="rounded-lg bg-amber-50/70 px-3 py-2 text-xs text-amber-800">
              <button type="button" onClick={() => setShowDrive((v) => !v)} className="inline-flex items-center gap-1 font-semibold">
                {showDrive ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {stats.driveOnly.length} document{stats.driveOnly.length === 1 ? " still opens" : "s still open"} from Google Drive
              </button>
              <span> — too big to move automatically. Drop each file on Team → Library → Upload with the same name and it moves in.</span>
              {showDrive && <ul className="mt-1.5 list-disc space-y-0.5 pl-6">{stats.driveOnly.map((n) => <li key={n}>{n}</li>)}</ul>}
            </div>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-slate-100 pt-3 text-xs">
          <button type="button" onClick={() => setNewNote(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-white px-3 py-1.5 font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50">
            <StickyNote className="h-3.5 w-3.5 text-amber-500" /> New Note
          </button>
          <Link to="/team?pane=library" className="inline-flex items-center gap-0.5 font-semibold text-brand-700 hover:text-brand-900">
            Open the Library <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
      </div>

      <div className="rounded-2xl border border-hairline bg-white p-5 shadow-e1">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-[13px] font-bold text-slate-800">Questions Asked</h3>
          <InfoTip label="About questions asked">
            People ask from the sparkle button on every page. Needs a Look holds answers marked wrong or that say it couldn't
            find something; Teach It writes a Library note that Ask Pegasus reads first, from the next question on.
          </InfoTip>
          <div className="ml-auto flex items-center gap-1.5">
            {([["look", `Needs a Look · ${lookCount}`], ["all", `All · ${rows.length}`]] as const).map(([k, label]) => (
              <button key={k} type="button" onClick={() => setFilter(k)}
                className={cn("rounded-full px-2.5 py-1 text-xs font-semibold transition-colors",
                  filter === k ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200")}>
                {label}
              </button>
            ))}
          </div>
        </div>

        {log.isLoading ? (
          <div className="mt-3 h-24 animate-pulse rounded-lg bg-slate-100" />
        ) : shown.length === 0 ? (
          <p className="mt-4 rounded-lg bg-slate-50 px-3 py-6 text-center text-sm text-slate-400">
            {filter === "look" ? "Nothing needs a look right now." : "No questions yet."}
          </p>
        ) : (
          <ul className="mt-3 divide-y divide-slate-100">
            {shown.map((r) => {
              const isOpen = open === r.id;
              const tools = [...new Set((r.tools ?? []).map((t) => TOOL_LABEL[t.tool] ?? t.tool))];
              return (
                <li key={r.id} className="py-2.5">
                  <div className="flex items-start gap-2">
                    <button type="button" onClick={() => setOpen(isOpen ? null : r.id)} className="mt-0.5 text-slate-400 hover:text-slate-600" aria-label={isOpen ? "Collapse" : "Show the answer"}>
                      {isOpen ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                    </button>
                    <div className="min-w-0 flex-1">
                      <button type="button" onClick={() => setOpen(isOpen ? null : r.id)} className="block text-left text-sm font-semibold text-slate-800 hover:text-brand-700">
                        {r.question}
                      </button>
                      <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-slate-400">
                        <span>{when(r.at)}</span>
                        {r.user_email && <span>· {r.user_email}</span>}
                        {tools.map((t) => <span key={t} className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-500">{t}</span>)}
                        {r.verdict === "right" && <span className="rounded bg-emerald-50 px-1.5 py-0.5 font-semibold text-emerald-700">Right</span>}
                        {r.verdict === "wrong" && <span className="rounded bg-rose-50 px-1.5 py-0.5 font-semibold text-rose-700">Wrong{r.verdict_by && r.verdict_by !== r.user_email ? "" : " — said the asker"}</span>}
                        {r.handled_at && <span className="rounded bg-sky-50 px-1.5 py-0.5 font-semibold text-sky-700">{r.note_id ? "Taught" : "Closed"}</span>}
                      </div>
                      <p className={cn("mt-1 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-600", !isOpen && "line-clamp-2")}>{plain(r.answer)}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-1">
                      <button type="button" title="The answer was right" disabled={verdict.isPending}
                        onClick={() => verdict.mutate({ id: r.id, verdict: r.verdict === "right" ? null : "right" })}
                        className={cn("rounded p-1.5 hover:bg-emerald-50", r.verdict === "right" ? "text-emerald-600" : "text-slate-300 hover:text-emerald-600")}>
                        <ThumbsUp className="h-3.5 w-3.5" />
                      </button>
                      <button type="button" title="The answer was wrong" disabled={verdict.isPending}
                        onClick={() => verdict.mutate({ id: r.id, verdict: r.verdict === "wrong" ? null : "wrong" })}
                        className={cn("rounded p-1.5 hover:bg-rose-50", r.verdict === "wrong" ? "text-rose-600" : "text-slate-300 hover:text-rose-600")}>
                        <ThumbsDown className="h-3.5 w-3.5" />
                      </button>
                      {!r.handled_at && (
                        <>
                          <button type="button" onClick={() => setTeach(r)} title="Write the right answer as a note — Ask Pegasus reads it first"
                            className="inline-flex items-center gap-1 rounded-md bg-white px-2 py-1 text-xs font-semibold text-slate-700 shadow-sm ring-1 ring-slate-200 hover:bg-slate-50">
                            <StickyNote className="h-3.5 w-3.5 text-amber-500" /> Teach It
                          </button>
                          {needsLook(r) && (
                            <button type="button" onClick={() => handle.mutate({ id: r.id })} title="Nothing to fix — take it off the Needs a Look list"
                              className="rounded p-1.5 text-slate-300 hover:bg-slate-100 hover:text-slate-600">
                              <X className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </>
                      )}
                      {r.handled_at && <Check className="h-4 w-4 text-sky-500" />}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <NoteEditorModal open={!!teach} onClose={() => setTeach(null)}
        initialTitle={teach?.question ?? ""} initialFolder={NOTES_FOLDER} folders={folders}
        hint={teach ? <>It answered: <span className="italic">{plain(teach.answer).slice(0, 400)}</span></> : undefined}
        onSaved={(id) => { if (teach) handle.mutate({ id: teach.id, noteId: id }); }} />
      <NoteEditorModal open={newNote} onClose={() => setNewNote(false)} folders={folders} />
    </div>
  );
}
